/**
 * Pure builder: "biggest movers" vs prior period from existing dashboard current/previous metrics.
 * No backend changes; percent ranking only when previous > 0 for that series.
 */

import type { DashboardMoverSeriesId } from '@/lib/dashboard/dashboardMoverDrilldownUrls'

export type DashboardMoversMetricsSlice = {
  totalOrdersReceived: number
  deliveredOrders: number
  returnedOrders: number
  totalRevenueAll: number
  totalRevenueDelivered: number
  totalExpenses: number
  netProfit: number
}

type SeriesDef = {
  id: DashboardMoverSeriesId
  shortLabel: string
  /** false = higher current is better; true = lower current is better */
  lowerIsBetter: boolean
  pick: (m: DashboardMoversMetricsSlice) => number
}

const SERIES: readonly SeriesDef[] = [
  { id: 'total_orders', shortLabel: 'Total orders', lowerIsBetter: false, pick: (m) => m.totalOrdersReceived },
  { id: 'delivered_orders', shortLabel: 'Delivered orders', lowerIsBetter: false, pick: (m) => m.deliveredOrders },
  { id: 'returned_orders', shortLabel: 'Returns', lowerIsBetter: true, pick: (m) => m.returnedOrders },
  { id: 'total_revenue_all', shortLabel: 'Total revenue (all)', lowerIsBetter: false, pick: (m) => m.totalRevenueAll },
  { id: 'delivered_revenue', shortLabel: 'Delivered revenue', lowerIsBetter: false, pick: (m) => m.totalRevenueDelivered },
  { id: 'total_expenses', shortLabel: 'Total expenses', lowerIsBetter: true, pick: (m) => m.totalExpenses },
  { id: 'total_profit', shortLabel: 'Total profit', lowerIsBetter: false, pick: (m) => m.netProfit },
]

function pctChangeVsPrior(current: number, previous: number): number {
  return ((current - previous) / previous) * 100
}

function formatSignedPctFromRaw(rawPct: number): string {
  const a = Math.abs(rawPct)
  if (a < 0.1) return '~0%'
  const sign = rawPct >= 0 ? '+' : '−'
  return `${sign}${a.toFixed(1)}%`
}

type ScoredEntry = SeriesDef & {
  score: number
  current: number
  previous: number
}

function formatImprovementLine(e: ScoredEntry): string {
  const raw = pctChangeVsPrior(e.current, e.previous)
  if (!e.lowerIsBetter) {
    return `${e.shortLabel} ${formatSignedPctFromRaw(raw)} vs prior`
  }
  const downPct = ((e.previous - e.current) / e.previous) * 100
  const mag = Math.abs(downPct) < 0.1 ? '~0' : downPct.toFixed(1)
  if (e.id === 'returned_orders') return `Fewer returns — about ${mag}% vs prior`
  if (e.id === 'total_expenses') return `Lower expenses — about ${mag}% vs prior`
  return `${e.shortLabel} down about ${mag}% vs prior`
}

function formatSetbackLine(e: ScoredEntry): string {
  const raw = pctChangeVsPrior(e.current, e.previous)
  if (!e.lowerIsBetter) {
    return `${e.shortLabel} ${formatSignedPctFromRaw(raw)} vs prior`
  }
  const mag = Math.abs(raw) < 0.1 ? '~0' : Math.abs(raw).toFixed(1)
  if (e.id === 'returned_orders') return `More returns — about ${mag}% vs prior`
  if (e.id === 'total_expenses') return `Higher expenses — about ${mag}% vs prior`
  return `${e.shortLabel} up about ${mag}% vs prior`
}

export type DashboardMoverBullet = {
  seriesId: DashboardMoverSeriesId
  text: string
}

export type DashboardMoversResult = {
  hasComparablePeriod: boolean
  improvements: DashboardMoverBullet[]
  setbacks: DashboardMoverBullet[]
}

const TOP_N = 2

/**
 * @param previous null = no prior snapshot; returns hasComparablePeriod false and empty lists.
 */
export function buildDashboardMovers(
  current: DashboardMoversMetricsSlice,
  previous: DashboardMoversMetricsSlice | null
): DashboardMoversResult {
  if (previous == null) {
    return { hasComparablePeriod: false, improvements: [], setbacks: [] }
  }

  const scored: ScoredEntry[] = []

  for (const def of SERIES) {
    const c = def.pick(current)
    const p = def.pick(previous)
    if (p === 0 && c === 0) continue
    if (p <= 0) continue

    const score = def.lowerIsBetter ? (p - c) / p : (c - p) / p
    if (!Number.isFinite(score) || score === 0) continue

    scored.push({ ...def, score, current: c, previous: p })
  }

  const improvements = scored
    .filter((e) => e.score > 0)
    .sort((a, b) => b.score - a.score || Math.abs(b.current - b.previous) - Math.abs(a.current - a.previous))
    .slice(0, TOP_N)
    .map((e) => ({ seriesId: e.id, text: formatImprovementLine(e) }))

  const setbacks = scored
    .filter((e) => e.score < 0)
    .sort((a, b) => a.score - b.score || Math.abs(b.current - b.previous) - Math.abs(a.current - a.previous))
    .slice(0, TOP_N)
    .map((e) => ({ seriesId: e.id, text: formatSetbackLine(e) }))

  return {
    hasComparablePeriod: true,
    improvements,
    setbacks,
  }
}

export function sliceMetricsForMovers(m: DashboardMoversMetricsSlice): DashboardMoversMetricsSlice {
  return {
    totalOrdersReceived: m.totalOrdersReceived,
    deliveredOrders: m.deliveredOrders,
    returnedOrders: m.returnedOrders,
    totalRevenueAll: m.totalRevenueAll,
    totalRevenueDelivered: m.totalRevenueDelivered,
    totalExpenses: m.totalExpenses,
    netProfit: m.netProfit,
  }
}
